import * as React from 'react';
import { SelectItem } from 'vet-bones/utils/select';

import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ModelConceptRelationshipLink } from 'src/containers/ProjectDiagram/types/diagram';
import { LinkData } from 'src/containers/ProjectDiagram/types/model';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { isLink } from 'src/containers/ProjectDiagram/utils/graphObject';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addDomainOrRangeToRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addDomainOrRangeToRelationship';
import { removeModelConceptRelationshipForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationshipForClass';
import { getProjectModelIdsForIri } from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { removePartsFromSelection } from 'src/containers/ProjectDiagram/utils/removePartsFromSelection';

export const useAddClassForRelationship = ({
  direction,
  relationshipIri,
}: {
  direction: RelationshipDirection;
  relationshipIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: SelectItem | null) => {
      if (!item || !diagram || !relationshipIri) {
        return;
      }

      const { value, isNewlyCreated, text } = item;
      commitDiagramModel(
        diagram,
        (model, diagram) => {
          if (isNewlyCreated) {
            addModelConceptClass({
              iri: value,
              label: text,
              projectModelIds: getProjectModelIdsForIri(relationshipIri, model),
            })(model);
          }
          addDomainOrRangeToRelationship({
            direction,
            relationshipIri,
            classIri: value,
          })(model, diagram);
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );
    },
    [diagram, direction, relationshipIri]
  );
};

export const useRemoveClassForRelationship = ({
  direction,
  relationshipIri,
}: {
  direction: RelationshipDirection;
  relationshipIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: SelectItem | null) => {
      if (!item || !diagram || !relationshipIri) {
        return;
      }

      const { value } = item;
      const classKey = getModelConceptClassKey(value);

      // Clearing canvas selection before the delete is a diagram-only affordance
      // (label nodes / selection have no headless equivalent); skip it headless.
      if (!diagram.headless) {
        const keysToRemove = new Set<go.Key>();
        diagram
          .findLinksByExample({
            value: relationshipIri,
            category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP,
            [direction === RelationshipDirection.OUTGOING ? 'from' : 'to']:
              classKey,
          })
          .each((link) => {
            keysToRemove.add(link.data.key);
            link.labelNodes.each((labelNode) => {
              keysToRemove.add(labelNode.data.key);
            });
          });

        const hasOtherDomainsOrRanges = diagram.links.any(
          (link) =>
            isLink<ModelConceptRelationshipLink>(
              link,
              LinkCategory.MODEL_CONCEPT_RELATIONSHIP
            ) &&
            link.data.value === relationshipIri &&
            link.data[
              direction === RelationshipDirection.OUTGOING ? 'from' : 'to'
            ] !== classKey
        );

        if (hasOtherDomainsOrRanges) {
          commitDiagramWhileSkippingUndoManager(
            diagram,
            (_, diagram) => removePartsFromSelection(keysToRemove)(diagram),
            DiagramTransaction.SET_DIAGRAM_SELECTION
          );
        }
      }

      // move away from unlinked relationships
      const layoutPartial = new Set([classKey]);
      const layoutLink = (linkData: LinkData) => {
        layoutPartial.add(linkData.key);
        linkData.labelKeys.forEach((labelKey) => {
          layoutPartial.add(labelKey);
        });
      };

      commitDiagramModel(
        diagram,
        (model) => {
          addDeletedData(
            model.linkDataArray.filter((linkData) => {
              if (
                isModelConceptRelationship(linkData) &&
                linkData.value === relationshipIri &&
                (direction === RelationshipDirection.INCOMING
                  ? linkData.to === classKey
                  : linkData.from === classKey)
              ) {
                layoutLink(linkData);
                return true;
              }
              return false;
            })
          )(model);
          removeModelConceptRelationshipForClass({
            direction,
            iri: relationshipIri,
            classIri: value,
          })(model);
        },
        {
          layout: DiagramModelCommitLayout.PARTIAL,
          layoutPartial,
        }
      );
    },
    [diagram, direction, relationshipIri]
  );
};
